import type { JSX } from 'react';

import type { PrefetchedDataProps, PrefetchedUserDataProps } from 'src/shared/prefetch';
import { withPrefetchedData } from 'src/shared/prefetch';

import {
	type PetitionDetailsPagePrefetchedContext,
	type PetitionDetailsPagePrefetchedUserContext,
	prefetchPageContext,
	prefetchPageUserContext,
} from 'src/app/pages/petitionDetails/pageContext';
import { headlineReplacements } from 'src/app/pages/petitionDetails/shared/utilities';
import { isLoaded } from 'src/app/shared/utils/async';

import { getFacebookExperimentHeadline } from '../api';
import { CommonPetitionDetails } from '../CommonPetitionDetails';
import {
	buildMetaContext,
	petitionDetailsPageAlternateUrls,
	petitionDetailsPageCanonical,
	petitionDetailsPageMetadata,
	petitionDetailsPageSchemaOrg,
	petitionDetailsPageTitle,
} from '../shared';
import type { PetitionDetailsMetaContext } from '../shared';

type PrefetchedData = PetitionDetailsPagePrefetchedContext &
	Readonly<{
		title: string;
		description: string;
		metaContext: PetitionDetailsMetaContext;
		bestShareVisual: boolean;
	}>;

export const PetitionDetailsFacebookExperiment = withPrefetchedData(
	({
		prefetchedData,
		prefetchedUserData,
	}: PrefetchedDataProps<PrefetchedData> &
		PrefetchedUserDataProps<PetitionDetailsPagePrefetchedUserContext>): JSX.Element => {
		return <CommonPetitionDetails prefetchedData={prefetchedData} prefetchedUserData={prefetchedUserData} />;
	},
	{
		prefetchName: __MODULE_HASH__,
		prefetchData: async (context) => {
			const {
				params: {
					slug: slugParam,
					experimentName: experimentNameParam,
					experimentVariantName: experimentVariantNameParam,
				},
			} = context;
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const slug = slugParam!;
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const experimentName = experimentNameParam!;
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const experimentVariantName = experimentVariantNameParam!;
			const [pageContext, experimentResult] = await Promise.all([
				prefetchPageContext(slug, context),
				getFacebookExperimentHeadline(experimentName, experimentVariantName, context.utilityContext).catch(() => ({
					title: '',
					description: '',
				})),
			]);
			const result = experimentResult ?? { title: '', description: '' };
			return {
				...pageContext,
				title: result.title,
				description: result.description,
				metaContext: buildMetaContext(context),
				bestShareVisual: context.path.endsWith('/bsv'),
			};
		},
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		prefetchUserData: async (context) => prefetchPageUserContext(context.params.slug!, context),
		pageTitle: (ctx) => petitionDetailsPageTitle(ctx),
		pageCanonical: (ctx) => petitionDetailsPageCanonical(ctx),
		pageMetadata: ({ prefetchedData, i18n }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			const replacements = headlineReplacements({
				pageData: prefetchedData.pageData.pageData,
				i18n,
			});
			return petitionDetailsPageMetadata({
				prefetchedData,
				i18n,
				metaContext: prefetchedData.metaContext,
				bestShareVisual: prefetchedData.bestShareVisual,
				shareHeadlineOverrides: {
					title: i18n.replacePlaceholders(prefetchedData.title, replacements),
					description: i18n.replacePlaceholders(prefetchedData.description, replacements),
				},
			});
		},
		pageSchemaOrg: (ctx) =>
			petitionDetailsPageSchemaOrg({
				prefetchedData: ctx.prefetchedData,
				resolveUrl: ctx.resolveUrl,
				defineArticle: ctx.defineArticle,
				defineBreadcrumb: ctx.defineBreadcrumb,
				definePerson: ctx.definePerson,
			}),
		pageAlternateUrls: (ctx) => petitionDetailsPageAlternateUrls(ctx),
	},
);
